import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import thermostatAuth from '../../../actions/thermostatAuth'
import { useAppSelector } from '../../../hooks'
import { ThermostatConnectFlowStep } from './ConnectThermostatFlow'
import { useNavigation } from '@/app/hooks'
import {
  useDeviceSelectionRedirectUrl,
  useDeviceSelectionRedirectUrlIsResolved,
} from '@/hooks/useDeviceSelectionRedirectUrl'
import { isNativeMobile } from '@/authenticated/hooks/useIsMobile'
import { surveyResponsesCollection } from '@/reducers/surveyResponses'
import { Brand, ThermostatBrand } from '@/types/brand'
import { ThermostatBrandName } from '@/types/thermostatBrands'
import { thermostatsCollection } from '@/reducers/thermostats'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'
import useEligibleThermostatBrands from '../ConnectThermostatSelector/useEligibleThermostatBrands'

function getThermostatConnectFlowStep(
  selectedBrand: Brand | undefined,
  thermostatConnected: boolean,
) {
  if (!selectedBrand) {
    return ThermostatConnectFlowStep.SelectThermostat
  } else if (!thermostatConnected) {
    return ThermostatConnectFlowStep.Permissions
  }
  return ThermostatConnectFlowStep.Confirmation
}

function useViewModel() {
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const [selectedBrand, setSelectedBrand] = useState<ThermostatBrand>()
  const thermostatConnected = useAppSelector((state) => {
    return [state.nestAuth, state.ecobeeAuth, state.honeywellAuth]
      .map((auth) => auth.success)
      .some(Boolean)
  })
  const { clearFlow } = useMultiProgramFlowControl()
  const url = useDeviceSelectionRedirectUrl(true)
  const urlIsResolved = useDeviceSelectionRedirectUrlIsResolved()

  const { brands, isLoadingOrIdle } = useEligibleThermostatBrands()
  // Latches once we auto-select, so that clearing the brand on back doesn't immediately
  // re-select it and trap the user on the permissions step. Also tells `onBackClick`
  // there is no brand picker behind the permissions step to return to.
  const skippedBrandSelection = useRef(false)

  // A program that accepts a single brand (e.g. Octopus Energy Texas is Nest-only) turns
  // brand selection into a one-option click, so skip straight to that brand's OAuth start.
  useEffect(() => {
    if (isLoadingOrIdle || selectedBrand || skippedBrandSelection.current) {
      return
    }
    if (brands.length !== 1) {
      return
    }
    skippedBrandSelection.current = true
    setSelectedBrand(brands[0])
  }, [brands, isLoadingOrIdle, selectedBrand])

  const onClose = () => {
    // Broadcast-independent refresh (e.g. if the OAuth-success broadcast is missed
    // on native mobile): invalidate so the device-selector galleries refetch.
    dispatch(thermostatsCollection.actions.invalidate())
    dispatch(surveyResponsesCollection.actions.fetch())
    clearFlow()
    navigation.push(url)
    setSelectedBrand(undefined)
    if (selectedBrand?.id) {
      dispatch(
        thermostatAuth(selectedBrand.id as ThermostatBrandName).actions.setAuth(
          false,
        ),
      )
    }
  }

  // Latches so the `setAuth(false)` inside `onClose` can't retrigger us.
  const autoContinued = useRef(false)

  // On web the OAuth tab is the success confirmation, and this tab is in the
  // background while the user reads it — so a confirmation step here is a second
  // screen they never see but still have to dismiss, which is how people ended up
  // clicking Continue twice. Continue the flow for them once the broadcast lands.
  // Native mobile has no second tab, so there the confirmation step is the only
  // success UI the user gets and it stays.
  useEffect(() => {
    if (isNativeMobile() || autoContinued.current) {
      return
    }
    if (!thermostatConnected || !urlIsResolved) {
      return
    }
    autoContinued.current = true
    onClose()
  }, [thermostatConnected, urlIsResolved])

  return {
    mode: getThermostatConnectFlowStep(selectedBrand, thermostatConnected),
    brand: selectedBrand,
    thermostatConnected,
    onBrandSelect(brand: ThermostatBrand) {
      setSelectedBrand(brand)
    },
    onClose,
    onBackClick() {
      const currentMode = getThermostatConnectFlowStep(
        selectedBrand,
        thermostatConnected,
      )
      switch (currentMode) {
        // Brand selection is now the first step of the flow, so back leaves it.
        case ThermostatConnectFlowStep.SelectThermostat:
          navigation.goBack()
          break
        case ThermostatConnectFlowStep.Permissions:
          // When brand selection was skipped there's no picker to return to.
          if (skippedBrandSelection.current) {
            navigation.goBack()
          } else {
            setSelectedBrand(undefined)
          }
          break
        default: // add other cases if need for other steps
      }
    },
  }
}

export default useViewModel
